/**
 * 🎯 CONFIGURATION COMPLÈTE DES PROFILS MÉTIER
 * 
 * Ce fichier définit l'interface et le comportement de l'Admin-Web
 * pour chaque type de commerce de manière DÉFINITIVE.
 * 
 * Chaque profil contient :
 * - Type de module principal (produits ou prestations)
 * - Champs à afficher dans les formulaires
 * - Labels personnalisés
 * - Validations spécifiques
 * - Modules complémentaires activés
 */

export const BUSINESS_PROFILES = {
  
  // ============================================================================
  // 🍕 RESTAURATION & ALIMENTATION
  // ============================================================================
  
  RESTAURANT: {
    type: 'restauration',
    module: 'produits',
    label: 'Restaurant',
    icon: '🍽️',
    
    dashboard: {
      titre: 'Gestion Restaurant',
      widgets: ['commandes', 'carte', 'stock', 'analytics'],
      stats: ['ca_jour', 'commandes_en_cours', 'plats_populaires', 'avis']
    },
    
    modules: {
      produits: {
        label: 'Ma Carte',
        labelSingulier: 'Plat',
        labelAjouter: 'Ajouter un plat',
        champs: [
          'nom', 'description', 'prix', 'prixPromo', 'image',
          'categorieProduit', 'allergenes', 'ingredients', 
          'complements', 'variantes', 'disponible', 'stock'
        ],
        groupes: [
          { nom: 'Informations de base', champs: ['nom', 'description', 'prix', 'prixPromo', 'image'] },
          { nom: 'Catégorie & Allergènes', champs: ['categorieProduit', 'allergenes'] },
          { nom: 'Composition', champs: ['ingredients', 'complements', 'variantes'] },
          { nom: 'Disponibilité', champs: ['disponible', 'stock'] }
        ]
      },
      commandes: { actif: true },
      stock: { actif: true },
      livraison: { actif: true },
      clickAndCollect: { actif: true },
      prestations: { actif: false }
    },
    
      features: {
        allergenes: true,
        ingredients: true,
        complements: true,
        variantes: true,
        stock: true,
        livraison: true,
        clickAndCollect: true,
        instructions: true,
        reservations: false,
        instructeurs: false,
        creneaux: false
      }
  },

  PIZZERIA: {
    type: 'restauration',
    module: 'produits',
    label: 'Pizzeria',
    icon: '🍕',
    extends: 'RESTAURANT',
    
    modules: {
      produits: {
        label: 'Mes Pizzas',
        labelSingulier: 'Pizza',
        labelAjouter: 'Ajouter une pizza',
        champsSupplementaires: ['taille', 'pate', 'base']
      }
    }
  },

  CAFE: {
    type: 'restauration',
    module: 'produits',
    label: 'Café / Bar',
    icon: '☕',
    extends: 'RESTAURANT',
    modules: {
      produits: { label: 'Carte boissons & snacks', labelSingulier: 'Article' },
    },
  },

  BOULANGERIE: {
    type: 'alimentation',
    module: 'produits',
    label: 'Boulangerie',
    icon: '🥖',
    
    dashboard: {
      titre: 'Gestion Boulangerie',
      widgets: ['ventes', 'stock', 'production'],
      stats: ['ca_jour', 'stock_pain', 'viennoiseries_vendues']
    },
    
    modules: {
      produits: {
        label: 'Mes Produits',
        labelSingulier: 'Produit',
        labelAjouter: 'Ajouter un produit',
        champs: [
          'nom', 'description', 'prix', 'prixPromo', 'image',
          'categorieProduit', 'allergenes', 'ingredients',
          'disponible', 'stock', 'heureFabrication'
        ]
      }
    },
    
    features: {
      allergenes: true,
      ingredients: true,
      stock: true,
      production: true,
      horaires: true,
      clickAndCollect: true,
      livraison: false,
      prestations: false
    }
  },

  // ============================================================================
  // 💇 BEAUTÉ & COIFFURE
  // ============================================================================

  SALON_BEAUTE: {
    type: 'services',
    module: 'prestations',
    label: 'Salon de Beauté',
    icon: '💅',
    
    dashboard: {
      titre: 'Gestion Salon de Beauté',
      widgets: ['reservations', 'planning', 'prestations', 'equipe'],
      stats: ['rdv_jour', 'ca_previsionnel', 'taux_remplissage', 'estheticiennes']
    },
    
    modules: {
      prestations: {
        label: 'Mes Prestations',
        labelSingulier: 'Prestation',
        labelAjouter: 'Ajouter une prestation',
        champs: [
          'nom', 'description', 'dureeMinutes', 'prix', 'prixPromo',
          'categorie', 'instructeurId', 'niveau', 'capaciteMax',
          'materielInclus', 'conditions', 'instructions', 'actif', 'image'
        ],
        groupes: [
          { nom: 'Informations', champs: ['nom', 'description', 'categorie', 'image'] },
          { nom: 'Tarification', champs: ['prix', 'prixPromo'] },
          { nom: 'Durée & Capacité', champs: ['dureeMinutes', 'capaciteMax'] },
          { nom: 'Esthéticienne', champs: ['instructeurId'] },
          { nom: 'Détails', champs: ['materielInclus', 'conditions', 'instructions', 'actif'] }
        ]
      },
      instructeurs: {
        label: 'Mes Esthéticiennes',
        labelSingulier: 'Esthéticienne',
        labelAjouter: 'Ajouter une esthéticienne',
        champs: [
          'nom', 'prenom', 'email', 'telephone',
          'specialites', 'certifications', 'bio', 'photo',
          'tarifHoraire', 'actif'
        ]
      },
      reservations: {
        label: 'Mes Rendez-vous',
        labelSingulier: 'Rendez-vous',
        afficherCalendrier: true
      },
      abonnements: {
        label: 'Forfaits & Abonnements',
        actif: true
      }
    },
    
    features: {
      reservations: true,
      instructeurs: true,
      creneaux: true,
      planning: true,
      abonnements: true,
      produits: false,
      allergenes: false,
      stock: false,
      livraison: false
    }
  },

  COIFFURE: {
    type: 'services',
    module: 'prestations',
    label: 'Salon de Coiffure',
    icon: '✂️',
    extends: 'SALON_BEAUTE',
    
    dashboard: {
      titre: 'Gestion Salon de Coiffure'
    },
    
    modules: {
      prestations: {
        label: 'Mes Services',
        categories: ['Coupe', 'Coloration', 'Coiffage', 'Soins', 'Barbe']
      },
      instructeurs: {
        label: 'Mes Coiffeurs',
        labelSingulier: 'Coiffeur',
        labelAjouter: 'Ajouter un coiffeur'
      }
    }
  },

  BARBIER: {
    type: 'services',
    module: 'prestations',
    label: 'Barbier',
    icon: '💈',
    extends: 'COIFFURE',
    
    modules: {
      prestations: {
        categories: ['Taille barbe', 'Rasage', 'Coupe', 'Soin barbe']
      },
      instructeurs: {
        label: 'Mes Barbiers',
        labelSingulier: 'Barbier'
      }
    }
  },

  NAIL_BAR: {
    type: 'services',
    module: 'prestations',
    label: 'Bar à Ongles',
    icon: '💅',
    extends: 'SALON_BEAUTE',
    
    modules: {
      prestations: {
        label: 'Mes Prestations Ongles',
        categories: ['Manucure', 'Pédicure', 'Pose d\'ongles', 'Nail Art', 'Soins']
      },
      instructeurs: {
        label: 'Mes Prothésistes',
        labelSingulier: 'Prothésiste ongulaire'
      }
    }
  },

  SPA: {
    type: 'services',
    module: 'prestations',
    label: 'Spa',
    icon: '🧖',
    extends: 'SALON_BEAUTE',
    
    modules: {
      prestations: {
        categories: ['Massages', 'Soins du corps', 'Soins du visage', 'Hammam', 'Sauna']
      },
      instructeurs: {
        label: 'Mes Praticiens',
        labelSingulier: 'Praticien'
      },
      ressources: {
        actif: true,
        label: 'Cabines & Équipements'
      }
    }
  },

  // ============================================================================
  // 🏃 SPORTS & LOISIRS
  // ============================================================================

  CENTRE_EQUESTRE: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Centre Équestre',
    icon: '🐴',
    
    dashboard: {
      titre: 'Gestion Centre Équestre',
      widgets: ['reservations', 'planning', 'chevaux', 'moniteurs'],
      stats: ['cours_jour', 'cavaliers_actifs', 'chevaux_disponibles', 'taux_occupation']
    },
    
    modules: {
      prestations: {
        label: 'Mes Cours & Activités',
        labelSingulier: 'Cours',
        labelAjouter: 'Ajouter un cours',
        champs: [
          'nom', 'description', 'dureeMinutes', 'prix', 'prixPromo',
          'niveau', 'ageMinimum', 'ageMaximum', 'capaciteMax',
          'instructeurId', 'equipementRequis', 'conditions', 'instructions', 'actif'
        ],
        categories: ['Cours débutant', 'Cours intermédiaire', 'Cours avancé', 'Balade', 'Stage', 'Compétition']
      },
      instructeurs: {
        label: 'Mes Moniteurs',
        labelSingulier: 'Moniteur',
        labelAjouter: 'Ajouter un moniteur',
        champsSpecifiques: ['diplomes', 'niveauxEnseignment', 'specialites']
      },
      ressources: {
        label: 'Mes Chevaux',
        labelSingulier: 'Cheval',
        actif: true,
        types: ['CHEVAL'],
        champs: ['nom', 'race', 'age', 'niveau', 'caractere', 'photo', 'disponible']
      },
      reservations: {
        label: 'Mes Réservations',
        afficherCalendrier: true,
        afficherRessources: true
      },
      abonnements: {
        label: 'Forfaits & Cartes',
        actif: true
      }
    },
    
    features: {
      reservations: true,
      instructeurs: true,
      ressources: true,
      creneaux: true,
      planning: true,
      abonnements: true,
      niveaux: true,
      ageMinMax: true,
      produits: false,
      livraison: false
    }
  },

  SALLE_SPORT: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Salle de Sport',
    icon: '🏋️',
    
    dashboard: {
      titre: 'Gestion Salle de Sport',
      widgets: ['planning', 'abonnements', 'membres', 'cours'],
      stats: ['membres_actifs', 'cours_jour', 'taux_presence', 'ca_mois']
    },
    
    modules: {
      prestations: {
        label: 'Mes Cours',
        categories: ['Fitness', 'Musculation', 'Cardio', 'Yoga', 'Pilates', 'CrossFit', 'Zumba']
      },
      instructeurs: {
        label: 'Mes Coachs',
        labelSingulier: 'Coach'
      },
      abonnements: {
        label: 'Abonnements',
        actif: true,
        types: ['Mensuel', 'Trimestriel', 'Annuel', 'Carte 10 séances']
      },
      membres: {
        label: 'Mes Membres',
        actif: true
      }
    }
  },

  PADEL: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Padel',
    icon: '🏓',
    modules: {
      prestations: {
        label: 'Réservations',
        categories: ['Location terrain', 'Cours', 'Tournoi', 'Stage'],
      },
      ressources: {
        label: 'Mes Terrains',
        actif: true,
        types: ['TERRAIN'],
      },
    },
    features: {
      reservations: true,
      ressources: true,
      creneaux: true,
      moduleCreneaux: true,
    },
  },

  FOOT: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Foot / Five',
    icon: '⚽',
    modules: {
      prestations: {
        label: 'Réservations',
        categories: ['Location terrain', 'Tournoi', 'Entreprise'],
      },
      ressources: {
        label: 'Mes Terrains',
        actif: true,
        types: ['TERRAIN'],
      },
    },
    features: {
      reservations: true,
      ressources: true,
      creneaux: true,
      moduleCreneaux: true,
    },
  },

  MULTISPORT: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Multisport / Loisirs',
    icon: '🎯',
    modules: {
      prestations: {
        label: 'Activités réservables',
        categories: ['Location', 'Cours', 'Stage', 'Anniversaire', 'Entreprise'],
      },
      ressources: {
        label: 'Mes Espaces',
        actif: true,
        types: ['TERRAIN', 'SALLE', 'EQUIPEMENT'],
      },
    },
    features: {
      reservations: true,
      ressources: true,
      creneaux: true,
      moduleCreneaux: true,
    },
  },

  TENNIS: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Club de Tennis',
    icon: '🎾',
    
    modules: {
      prestations: {
        label: 'Cours & Réservations Terrains',
        categories: ['Cours particulier', 'Cours collectif', 'Stage', 'Location terrain']
      },
      instructeurs: {
        label: 'Mes Professeurs',
        labelSingulier: 'Professeur'
      },
      ressources: {
        label: 'Mes Terrains',
        actif: true,
        types: ['TERRAIN'],
        champs: ['nom', 'type', 'surface', 'eclairage', 'couvert', 'disponible']
      }
    }
  },

  GOLF: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Golf',
    icon: '⛳',
    
    modules: {
      prestations: {
        categories: ['Green fee', 'Cours', 'Stage', 'Location matériel', 'Abonnement']
      },
      instructeurs: {
        label: 'Mes Pros',
        labelSingulier: 'Pro de golf'
      }
    }
  },

  PISCINE: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Piscine / Aquatique',
    icon: '🏊',
    
    modules: {
      prestations: {
        categories: ['Cours natation', 'Aquagym', 'Aquabike', 'Bébés nageurs', 'Accès libre']
      },
      instructeurs: {
        label: 'Mes Maîtres-nageurs',
        labelSingulier: 'Maître-nageur'
      }
    }
  },

  ESCALADE: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Escalade',
    icon: '🧗',
    extends: 'MULTISPORT',
    features: {
      reservations: true,
      ressources: true,
      creneaux: true,
      moduleCreneaux: true,
    },
  },

  KARTING: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Karting',
    icon: '🏎️',
    extends: 'MULTISPORT',
    features: {
      reservations: true,
      ressources: true,
      creneaux: true,
      moduleCreneaux: true,
    },
  },

  // ============================================================================
  // 🛍️ RETAIL & COMMERCE
  // ============================================================================

  BOUTIQUE: {
    type: 'retail',
    module: 'produits',
    label: 'Boutique',
    icon: '🛍️',
    
    dashboard: {
      titre: 'Gestion Boutique',
      widgets: ['ventes', 'stock', 'produits', 'analytics'],
      stats: ['ca_jour', 'ventes', 'stock_bas', 'produits_populaires']
    },
    
    modules: {
      produits: {
        label: 'Mon Catalogue',
        labelSingulier: 'Produit',
        labelAjouter: 'Ajouter un produit',
        champs: [
          'nom', 'description', 'prix', 'prixPromo', 'image',
          'categorieProduit', 'marque', 'reference', 'codeEAN',
          'stock', 'stockMin', 'variantes', 'tailles', 'couleurs',
          'disponible', 'nouveaute', 'promotion'
        ],
        groupes: [
          { nom: 'Informations produit', champs: ['nom', 'description', 'marque', 'reference', 'image'] },
          { nom: 'Prix & Promotion', champs: ['prix', 'prixPromo', 'promotion', 'nouveaute'] },
          { nom: 'Stock & Gestion', champs: ['stock', 'stockMin', 'disponible'] },
          { nom: 'Variantes', champs: ['variantes', 'tailles', 'couleurs'] }
        ]
      },
      stock: { actif: true },
      commandes: { actif: true },
      clickAndCollect: { actif: true },
      livraison: { actif: true }
    },
    
    features: {
      stock: true,
      variantes: true,
      tailles: true,
      couleurs: true,
      marques: true,
      livraison: true,
      clickAndCollect: true,
      allergenes: false,
      prestations: false
    }
  },

  FLEURISTE: {
    type: 'retail',
    module: 'produits',
    label: 'Fleuriste',
    icon: '💐',
    extends: 'BOUTIQUE',
    
    modules: {
      produits: {
        label: 'Mes Créations',
        categories: ['Bouquets', 'Compositions', 'Plantes', 'Deuil', 'Mariage', 'Événements']
      }
    },
    
    features: {
      personnalisation: true,
      messageCarte: true,
      livraison: true
    }
  },

  // ============================================================================
  // 🏥 SERVICES PROFESSIONNELS
  // ============================================================================

  CABINET_MEDICAL: {
    type: 'services',
    module: 'prestations',
    label: 'Cabinet Médical',
    icon: '🏥',
    
    dashboard: {
      titre: 'Gestion Cabinet',
      widgets: ['rdv', 'patients', 'planning'],
      stats: ['rdv_jour', 'patients_jour', 'taux_occupation']
    },
    
    modules: {
      prestations: {
        label: 'Mes Consultations',
        labelSingulier: 'Consultation',
        champs: ['nom', 'dureeMinutes', 'tarif', 'type']
      },
      instructeurs: {
        label: 'Mes Praticiens',
        labelSingulier: 'Praticien'
      },
      reservations: {
        label: 'Mes Rendez-vous',
        afficherCalendrier: true,
        confirmationEmail: true,
        rappelSMS: true
      }
    },
    
    features: {
      reservations: true,
      planning: true,
      confirmations: true,
      rappels: true,
      produits: false
    }
  },

  SERVICE: {
    type: 'services',
    module: 'prestations',
    label: 'Service professionnel',
    icon: '🔧',
    extends: 'CABINET_MEDICAL',
  },

  // ============================================================================
  // 🎨 CULTURE & ÉDUCATION
  // ============================================================================

  ATELIER_CREATIF: {
    type: 'loisirs',
    module: 'prestations',
    label: 'Atelier Créatif',
    icon: '🎨',
    
    modules: {
      prestations: {
        label: 'Mes Ateliers',
        categories: ['Peinture', 'Poterie', 'Sculpture', 'Dessin', 'DIY']
      },
      instructeurs: {
        label: 'Mes Animateurs',
        labelSingulier: 'Animateur'
      }
    }
  },

  ECOLE_MUSIQUE: {
    type: 'loisirs',
    module: 'prestations',
    label: 'École de Musique',
    icon: '🎵',
    
    modules: {
      prestations: {
        label: 'Mes Cours',
        categories: ['Piano', 'Guitare', 'Violon', 'Chant', 'Batterie', 'Solfège']
      },
      instructeurs: {
        label: 'Mes Professeurs',
        labelSingulier: 'Professeur'
      },
      ressources: {
        label: 'Salles & Instruments',
        actif: true
      }
    }
  },

  ECOLE_DANSE: {
    type: 'loisirs',
    module: 'prestations',
    label: 'École de Danse',
    icon: '💃',
    
    modules: {
      prestations: {
        label: 'Mes Cours',
        categories: ['Classique', 'Moderne', 'Hip-hop', 'Salsa', 'Contemporain']
      },
      instructeurs: {
        label: 'Mes Professeurs',
        labelSingulier: 'Professeur'
      }
    }
  }
};

/**
 * Récupère le profil métier d'une catégorie
 * Gère l'héritage avec "extends"
 */
const CATEGORIE_ID_ALIASES = { PADDLE: 'PADEL' };

function normalizeCategorieId(categorieId) {
  const key = String(categorieId || '').toUpperCase();
  return CATEGORIE_ID_ALIASES[key] || key;
}

export function getBusinessProfile(categorie) {
  if (!categorie) return null;
  const key = normalizeCategorieId(categorie);
  if (!BUSINESS_PROFILES[key]) {
    return null;
  }

  let profile = { ...BUSINESS_PROFILES[key] };
  
  // Gestion de l'héritage
  if (profile.extends) {
    const parentProfile = getBusinessProfile(profile.extends);
    profile = {
      ...parentProfile,
      ...profile,
      modules: {
        ...parentProfile.modules,
        ...profile.modules
      },
      features: {
        ...parentProfile.features,
        ...profile.features
      }
    };
  }

  return profile;
}

/**
 * Détermine si un commerce utilise des produits ou des prestations
 */
export function getModuleType(categorie) {
  const profile = getBusinessProfile(categorie);
  return profile?.module || 'produits';
}

/**
 * Liste toutes les catégories disponibles par type
 */
export function getCategoriesByType() {
  const types = {
    restauration: [],
    services: [],
    loisirs: [],
    retail: [],
    alimentation: []
  };

  Object.entries(BUSINESS_PROFILES).forEach(([key, profile]) => {
    const type = profile.type;
    if (types[type]) {
      types[type].push({
        key,
        label: profile.label,
        icon: profile.icon
      });
    }
  });

  return types;
}

export default BUSINESS_PROFILES;

